"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, Clock, PhoneCall, MapPin, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "item-1",
  question: "Do you offer 24/7 emergency plumbing service in Riverdale, GA?",
  answer: "Yes, our licensed technicians are on standby 24 hours a day, 7 days a week, including holidays and weekends. Whether you have an active burst pipe at midnight or a major drain backup on Sunday morning in Riverdale, we dispatch an emergency plumber immediately."
}, {
  id: "item-2",
  question: "What is your average response time for Riverdale service calls?",
  answer: "Our typical response time across Riverdale is 45 to 60 minutes for urgent plumbing emergencies. Because our dispatch trucks are stationed locally across Clayton County, we can navigate GA-85, GA-138, and surrounding neighborhood streets quickly."
}, {
  id: "item-3",
  question: "Which specific plumbing services are available to Riverdale homeowners?",
  answer: "We handle complete residential and light commercial plumbing needs: burst pipe repair, water heater replacement (tank and tankless), sewer line unblocking, main water line leak detection, toilet and fixture repairs, and high-pressure drain cleaning."
}, {
  id: "item-4",
  question: "What are your exact service area boundaries around Riverdale?",
  answer: "We serve the entire City of Riverdale and adjacent Clayton & South Fulton communities. This includes neighborhoods near Upper Riverdale Rd, Valley Hill Rd, Powers St, Church St, Helmer Rd, along with nearby borders of Jonesboro, College Park, Morrow, and Forest Park."
}, {
  id: "item-5",
  question: "How do I request emergency dispatch or regular service?",
  answer: "For immediate plumbing emergencies, call our direct 24/7 hotline directly to speak with a dispatcher. For scheduled maintenance, inspections, or non-urgent replacements, you can also submit a request through our online contact form."
}, {
  id: "item-6",
  question: "Are there upfront estimates before any repair work starts?",
  answer: "Yes. Once our technician arrives on site and assesses the plumbing problem, you receive an upfront, transparent estimate before any work begins. There are no surprise hidden diagnostic charges once the repair solution is agreed upon."
}];
export default function Riverdalefaq() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="riverdalefaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex flex-col items-center text-center mb-12">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }}>
            <Badge variant="secondary" className="mb-4 gap-1.5 py-1 px-3.5 text-xs font-semibold rounded-full border border-border">
              <MapPin className="size-3.5 text-accent-foreground" />
              <span>Riverdale Service Area FAQ</span>
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground max-w-2xl">
            Frequently Asked Questions in Riverdale
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.2
        }} className="mt-4 text-muted-foreground text-base md:text-lg max-w-2xl">
            Clear answers about emergency response times, local coverage areas, and service guarantees for Riverdale and Clayton County residents.
          </motion.p>
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="bg-card text-card-foreground border border-border rounded-2xl p-6 md:p-8 shadow-sm">
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
            {faqList.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-b border-border py-2 first:pt-0 last:border-b-0">
                <AccordionTrigger className="text-left font-default font-semibold text-base md:text-lg text-foreground hover:no-underline hover:text-primary transition-colors py-4">
                  <div className="flex items-center gap-3 pr-2">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-xs font-bold">
                      {i + 1}
                    </span>
                    <span>{faq.question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed pl-10 pr-2 pt-1 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.4
      }} className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div data-index={0} className="bg-secondary/40 border border-border rounded-xl p-5 flex items-start gap-3">
            <Clock className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-default font-semibold text-sm text-foreground">Rapid Dispatch</h3>
              <p className="text-xs text-muted-foreground mt-1">Average 45-60 minute arrival window for active Riverdale emergencies.</p>
            </div>
          </div>

          <div data-index={1} className="bg-secondary/40 border border-border rounded-xl p-5 flex items-start gap-3">
            <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-default font-semibold text-sm text-foreground">Licensed & Insured</h3>
              <p className="text-xs text-muted-foreground mt-1">Experienced plumbers carrying full coverage and clean workmanship guarantees.</p>
            </div>
          </div>

          <div data-index={2} className="bg-secondary/40 border border-border rounded-xl p-5 flex items-start gap-3">
            <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-default font-semibold text-sm text-foreground">Transparent Pricing</h3>
              <p className="text-xs text-muted-foreground mt-1">Clear quotes presented prior to work so you stay fully informed.</p>
            </div>
          </div>
        </motion.div>

        <div className="mt-12 text-center bg-primary text-primary-foreground rounded-2xl p-8 md:p-10 shadow-md">
          <div className="max-w-xl mx-auto space-y-4">
            <h3 className="font-default text-2xl md:text-3xl font-bold">
              Have a plumbing emergency right now?
            </h3>
            <p className="text-primary-foreground/90 text-sm md:text-base">
              Our technicians in Riverdale and Jonesboro are standing by to stop leaks and clear blockages.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-sm">
                <Link to="/contact/" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call Emergency Dispatch</span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/services/" className="inline-flex items-center gap-2">
                  <span>View All Services</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}